import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/AuthContext';
import { AccountIdentity } from '../../design-system/components';
import { Icon } from '../Icon';

export function AccountMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const outside = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);
  const keys = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false);
      trigger.current?.focus();
    }
    if (event.key === 'Tab') setOpen(false);
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const items = Array.from(root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? items.length - 1
          : (at + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
  };
  if (!user) return null;
  const links = [
    [`/user/${user.username}`, 'Your profile', 'user'],
    ['/settings/wikis', 'Your wikis', 'book'],
    ['/settings/starred', 'Saved wikis', 'star'],
    ['/settings/following', 'Following', 'chat'],
    ['/settings', 'Account settings', 'settings'],
    ['/settings/appearance', 'Appearance', 'sun'],
    ['/download', 'Download Athenaeum', 'download'],
  ] as const;
  return (
    <div className="account-menu" ref={root}>
      <button
        className="btn ghost account-trigger"
        ref={trigger}
        aria-label={`Account menu for ${user.username}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen(!open)}
      >
        <AccountIdentity username={user.displayName || user.username} detail={`@${user.username}`} />
        <Icon name="chevronDown" />
      </button>
      {open && (
        <div className="account-popover" role="menu" aria-label="Account" onKeyDown={keys}>
          <div className="account-popover-heading">
            <AccountIdentity username={user.displayName || user.username} detail={`@${user.username}`} />
          </div>
          {links.map(([to, label, icon]) => (
            <Link key={to} to={to} role="menuitem" onClick={() => setOpen(false)}>
              <Icon name={icon} />
              {label}
            </Link>
          ))}
          {error && (
            <p className="alert error" role="alert">
              {error}
            </p>
          )}
          <button
            role="menuitem"
            disabled={busy}
            className="sign-out"
            onClick={async () => {
              setBusy(true);
              setError('');
              try {
                await signOut(() => navigate('/', { replace: true }));
                setOpen(false);
              } catch (cause) {
                setError(cause instanceof Error ? cause.message : 'Could not sign out. Try again.');
              } finally {
                setBusy(false);
              }
            }}
          >
            <Icon name="logout" />
            {busy ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}
